import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// Unified silver/gray palette -- helmet and body share the same tones
const SILVER = "#d5d0c8";
const SILVER_MID = "#c0bbb3";
const SILVER_SHADE = "#aaa59d";
// Face: slightly darker than helmet but not a black void
const FACE = "#78736b";
const FACE_DARK = "#605b54";
// Accents
const COLLAR = "#3a3835";
const VISOR = "#30e878";
const PLUME_DEFAULT = "#e74c3c";
const SCARF = "#b33528";
const JOINT = "#555050";

const H = 0.035;
const E = "sine" as const;
const HP = [0, 0.33 + H, 0] as const;   // head pivot
const ALP = [-0.12, 0.215, 0] as const; // arm-l pivot (shoulder)
const ARP = [0.12, 0.215, 0] as const;  // arm-r pivot (shoulder)
const LLP = [-0.04, 0.078, 0] as const; // leg-l pivot (hip)
const LRP = [0.04, 0.078, 0] as const;  // leg-r pivot (hip)
const BP = [0, 0.15, 0] as const;       // body pivot (torso center)

function helmet(): Part[] {
  return [
    // dome: smooth sphere, bright silver
    { geometry: new THREE.SphereGeometry(0.088, 10, 7),
      color: SILVER, position: [0, 0.33 + H, 0], scale: [1, 0.75, 1], name: "head" },

    // face: a rounded cylinder covering the front of the head
    // slightly darker than the dome so the visor reads
    { geometry: new THREE.CylinderGeometry(0.072, 0.065, 0.07, 8),
      color: FACE, position: [0, 0.295 + H, 0.025], name: "head" },
    // face front plate (fills in the flat face surface)
    { geometry: new THREE.BoxGeometry(0.11, 0.065, 0.02),
      color: FACE, position: [0, 0.295 + H, 0.065], name: "head" },

    // visor: single continuous bright green slit wrapping across the face
    { geometry: new THREE.BoxGeometry(0.16, 0.022, 0.02),
      color: VISOR, position: [0, 0.305 + H, 0.073], name: "head" },
    // visor wrap left
    { geometry: new THREE.BoxGeometry(0.02, 0.022, 0.04),
      color: VISOR, position: [-0.075, 0.305 + H, 0.055], name: "head" },
    // visor wrap right
    { geometry: new THREE.BoxGeometry(0.02, 0.022, 0.04),
      color: VISOR, position: [0.075, 0.305 + H, 0.055], name: "head" },

    // chin: rounded, tapers down, same silver as dome
    { geometry: new THREE.CylinderGeometry(0.065, 0.05, 0.04, 8),
      color: SILVER_MID, position: [0, 0.248 + H, 0], name: "head" },
    // chin front (small plate below the face)
    { geometry: new THREE.BoxGeometry(0.08, 0.025, 0.025),
      color: SILVER_MID, position: [0, 0.255 + H, 0.045], name: "head" },

    // ear disc left
    { geometry: new THREE.CylinderGeometry(0.034, 0.034, 0.016, 8),
      color: SILVER_MID, position: [-0.08, 0.295 + H, 0.01], rotation: [0, 0, Math.PI / 2], name: "head" },
    { geometry: new THREE.CylinderGeometry(0.018, 0.018, 0.022, 6),
      color: FACE_DARK, position: [-0.088, 0.295 + H, 0.01], rotation: [0, 0, Math.PI / 2], name: "head" },
    // ear disc right
    { geometry: new THREE.CylinderGeometry(0.034, 0.034, 0.016, 8),
      color: SILVER_MID, position: [0.08, 0.295 + H, 0.01], rotation: [0, 0, Math.PI / 2], name: "head" },
    { geometry: new THREE.CylinderGeometry(0.018, 0.018, 0.022, 6),
      color: FACE_DARK, position: [0.088, 0.295 + H, 0.01], rotation: [0, 0, Math.PI / 2], name: "head" },

    // plume mount strip
    { geometry: new THREE.BoxGeometry(0.03, 0.012, 0.06),
      color: COLLAR, position: [0, 0.37 + H, -0.04], name: "head" },
    // plume root
    { geometry: new THREE.BoxGeometry(0.055, 0.06, 0.08),
      color: PLUME_DEFAULT, position: [0, 0.37 + H, -0.07], rotation: [0.5, 0, 0], name: "head", slot: "team" },
    // plume main sweep
    { geometry: new THREE.BoxGeometry(0.06, 0.10, 0.14),
      color: PLUME_DEFAULT, position: [0, 0.35 + H, -0.15], rotation: [0.7, 0, 0], name: "head", slot: "team" },
    // plume tail
    { geometry: new THREE.BoxGeometry(0.05, 0.07, 0.12),
      color: PLUME_DEFAULT, position: [0, 0.30 + H, -0.23], rotation: [0.85, 0, 0], name: "head", slot: "team" },
  ];
}

function neck(): Part[] {
  return [
    { geometry: new THREE.CylinderGeometry(0.05, 0.055, 0.02, 8),
      color: SCARF, position: [0, 0.245, 0], name: "body" },
  ];
}

function scarf(): Part[] {
  return [
    { geometry: new THREE.CylinderGeometry(0.065, 0.068, 0.035, 7),
      color: SCARF, position: [0, 0.232, 0], name: "body" },
    { geometry: new THREE.ConeGeometry(0.04, 0.07, 3),
      color: SCARF, position: [0, 0.19, 0.05], rotation: [0.08, 0, 0], name: "body" },
  ];
}

function torso(): Part[] {
  return [
    // main chest (same silver as helmet)
    { geometry: new THREE.CylinderGeometry(0.08, 0.088, 0.15, 8),
      color: SILVER_MID, position: [0, 0.16, 0], name: "body" },
    // chest plate front
    { geometry: new THREE.BoxGeometry(0.11, 0.09, 0.022),
      color: SILVER, position: [0, 0.18, 0.075], name: "body" },
    // center seam
    { geometry: new THREE.BoxGeometry(0.006, 0.08, 0.008),
      color: SILVER_SHADE, position: [0, 0.18, 0.087], name: "body" },
    // belly plate
    { geometry: new THREE.BoxGeometry(0.07, 0.035, 0.018),
      color: SILVER, position: [0, 0.12, 0.078], name: "body" },
    // back plate
    { geometry: new THREE.BoxGeometry(0.10, 0.10, 0.02),
      color: SILVER_SHADE, position: [0, 0.17, -0.072], name: "body" },
    // belt
    { geometry: new THREE.CylinderGeometry(0.085, 0.078, 0.025, 8),
      color: SILVER_SHADE, position: [0, 0.09, 0], name: "body" },
    // left shoulder pad
    { geometry: new THREE.SphereGeometry(0.048, 6, 4),
      color: SILVER, position: [-0.115, 0.215, 0], name: "body" },
    // right shoulder pad
    { geometry: new THREE.SphereGeometry(0.048, 6, 4),
      color: SILVER, position: [0.115, 0.215, 0], name: "body" },
  ];
}

function arms(): Part[] {
  return [
    { geometry: new THREE.CylinderGeometry(0.03, 0.032, 0.06, 6),
      color: SILVER_MID, position: [-0.12, 0.175, 0], name: "arm-l" },
    { geometry: new THREE.SphereGeometry(0.024, 5, 3),
      color: JOINT, position: [-0.12, 0.14, 0], name: "arm-l" },
    { geometry: new THREE.CylinderGeometry(0.028, 0.03, 0.055, 6),
      color: SILVER_SHADE, position: [-0.12, 0.11, 0], name: "arm-l" },
    { geometry: new THREE.BoxGeometry(0.032, 0.032, 0.032),
      color: JOINT, position: [-0.12, 0.078, 0], name: "arm-l" },

    { geometry: new THREE.CylinderGeometry(0.03, 0.032, 0.06, 6),
      color: SILVER_MID, position: [0.12, 0.175, 0], name: "arm-r" },
    { geometry: new THREE.SphereGeometry(0.024, 5, 3),
      color: JOINT, position: [0.12, 0.14, 0], name: "arm-r" },
    { geometry: new THREE.CylinderGeometry(0.028, 0.03, 0.055, 6),
      color: SILVER_SHADE, position: [0.12, 0.11, 0], name: "arm-r" },
    { geometry: new THREE.BoxGeometry(0.032, 0.032, 0.032),
      color: JOINT, position: [0.12, 0.078, 0], name: "arm-r" },
  ];
}

function legs(): Part[] {
  return [
    { geometry: new THREE.CylinderGeometry(0.035, 0.03, 0.055, 6),
      color: SILVER_MID, position: [-0.04, 0.06, 0], name: "leg-l" },
    { geometry: new THREE.SphereGeometry(0.024, 5, 3),
      color: JOINT, position: [-0.04, 0.033, 0], name: "leg-l" },
    { geometry: new THREE.CylinderGeometry(0.028, 0.032, 0.038, 6),
      color: SILVER_SHADE, position: [-0.04, 0.015, 0], name: "leg-l" },
    { geometry: new THREE.BoxGeometry(0.04, 0.014, 0.052),
      color: SILVER_SHADE, position: [-0.04, 0.003, 0.008], name: "leg-l" },

    { geometry: new THREE.CylinderGeometry(0.035, 0.03, 0.055, 6),
      color: SILVER_MID, position: [0.04, 0.06, 0], name: "leg-r" },
    { geometry: new THREE.SphereGeometry(0.024, 5, 3),
      color: JOINT, position: [0.04, 0.033, 0], name: "leg-r" },
    { geometry: new THREE.CylinderGeometry(0.028, 0.032, 0.038, 6),
      color: SILVER_SHADE, position: [0.04, 0.015, 0], name: "leg-r" },
    { geometry: new THREE.BoxGeometry(0.04, 0.014, 0.052),
      color: SILVER_SHADE, position: [0.04, 0.003, 0.008], name: "leg-r" },
  ];
}

function cape(): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(0.13, 0.10, 0.014),
      color: PLUME_DEFAULT, position: [0, 0.17, -0.082], name: "body", slot: "team" },
    { geometry: new THREE.BoxGeometry(0.12, 0.12, 0.012),
      color: PLUME_DEFAULT, position: [0, 0.07, -0.087], rotation: [0.05, 0, 0], name: "body", slot: "team" },
  ];
}

function knightAvatar(): Part[] {
  return [
    ...legs(),
    ...torso(),
    ...arms(),
    ...neck(),
    ...scarf(),
    ...cape(),
    ...helmet(),
  ];
}

export const playerKnight = defineModel({
  id: "player-knight",
  batchId: "characters",
  name: "Player Knight",
  tags: ["character", "avatar", "humanoid", "person", "player", "knight", "robot", "armored"],
  summary: "An armored knight-robot avatar with dome helmet, green visor, team-coloured plume and cape.",
  description:
    "A chunky low-poly knight-robot player avatar inspired by armored sentinels: " +
    "a smooth silver dome helmet with a rounded darker face housing a bright green " +
    "visor slit, round ear discs, a flowing plume and cape in the player's team " +
    "colour, a dark collar ring, a red triangular scarf, and matching silver plate " +
    "armor across the body with shoulder pads and chunky segmented limbs.",
  author: "models",
  footprint: 0.13,
  variants: [
    { id: "default", label: "Standing", description: "The default standing pose.", parts: knightAvatar },
  ],
  sockets: [
    { id: "carry", position: [0, 0.15, 0.14], slotType: "carry", description: "A good cradled in front of the chest." },
  ],
  animations: [
    // ── 1. idle ──
    { id: "idle", label: "Idle bob", duration: 2.0, tracks: [
      { target: "head", pivot: HP, position: [
        { t: 0, value: [0, 0, 0] }, { t: 1, value: [0, 0.005, 0], ease: E }, { t: 2, value: [0, 0, 0], ease: E }] },
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 1, value: [0.015, 0, 0], ease: E }, { t: 2, value: [0, 0, 0], ease: E }] },
      { target: "arm-l", pivot: ALP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 1.3, value: [0, 0, -0.04], ease: E }, { t: 2, value: [0, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 1.3, value: [0, 0, 0.04], ease: E }, { t: 2, value: [0, 0, 0], ease: E }] },
    ] },
    // ── 2. walk ──
    { id: "walk", label: "Walk", duration: 0.8, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0.04, 0, 0.02] }, { t: 0.4, value: [0.04, 0, -0.02], ease: E }, { t: 0.8, value: [0.04, 0, 0.02], ease: E }] },
      { target: "leg-l", pivot: LLP, rotation: [
        { t: 0, value: [0.3, 0, 0] }, { t: 0.4, value: [-0.3, 0, 0], ease: E }, { t: 0.8, value: [0.3, 0, 0], ease: E }] },
      { target: "leg-r", pivot: LRP, rotation: [
        { t: 0, value: [-0.3, 0, 0] }, { t: 0.4, value: [0.3, 0, 0], ease: E }, { t: 0.8, value: [-0.3, 0, 0], ease: E }] },
      { target: "arm-l", pivot: ALP, rotation: [
        { t: 0, value: [-0.25, 0, 0] }, { t: 0.4, value: [0.25, 0, 0], ease: E }, { t: 0.8, value: [-0.25, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [0.25, 0, 0] }, { t: 0.4, value: [-0.25, 0, 0], ease: E }, { t: 0.8, value: [0.25, 0, 0], ease: E }] },
      { target: "head", pivot: HP, position: [
        { t: 0, value: [0, 0, 0] }, { t: 0.2, value: [0, 0.006, 0], ease: E }, { t: 0.4, value: [0, 0, 0], ease: E },
        { t: 0.6, value: [0, 0.006, 0], ease: E }, { t: 0.8, value: [0, 0, 0], ease: E }] },
    ] },
    // ── 3. run ──
    { id: "run", label: "Run", duration: 0.5, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0.1, 0, 0.03] }, { t: 0.25, value: [0.1, 0, -0.03], ease: E }, { t: 0.5, value: [0.1, 0, 0.03], ease: E }] },
      { target: "body", pivot: BP, position: [
        { t: 0, value: [0, 0, 0] }, { t: 0.125, value: [0, 0.008, 0], ease: E }, { t: 0.25, value: [0, 0, 0], ease: E },
        { t: 0.375, value: [0, 0.008, 0], ease: E }, { t: 0.5, value: [0, 0, 0], ease: E }] },
      { target: "leg-l", pivot: LLP, rotation: [
        { t: 0, value: [0.5, 0, 0] }, { t: 0.25, value: [-0.5, 0, 0], ease: E }, { t: 0.5, value: [0.5, 0, 0], ease: E }] },
      { target: "leg-r", pivot: LRP, rotation: [
        { t: 0, value: [-0.5, 0, 0] }, { t: 0.25, value: [0.5, 0, 0], ease: E }, { t: 0.5, value: [-0.5, 0, 0], ease: E }] },
      { target: "arm-l", pivot: ALP, rotation: [
        { t: 0, value: [-0.4, 0, 0] }, { t: 0.25, value: [0.4, 0, 0], ease: E }, { t: 0.5, value: [-0.4, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [0.4, 0, 0] }, { t: 0.25, value: [-0.4, 0, 0], ease: E }, { t: 0.5, value: [0.4, 0, 0], ease: E }] },
      { target: "head", pivot: HP, position: [
        { t: 0, value: [0, 0, 0] }, { t: 0.125, value: [0, 0.01, 0], ease: E }, { t: 0.25, value: [0, 0, 0], ease: E },
        { t: 0.375, value: [0, 0.01, 0], ease: E }, { t: 0.5, value: [0, 0, 0], ease: E }] },
    ] },
    // ── 4. sneak ──
    { id: "sneak", label: "Sneak", duration: 1.4, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0.12, 0, 0] }, { t: 0.7, value: [0.1, 0, 0], ease: E }, { t: 1.4, value: [0.12, 0, 0], ease: E }] },
      { target: "leg-l", pivot: LLP, rotation: [
        { t: 0, value: [0.15, 0, 0] }, { t: 0.7, value: [-0.15, 0, 0], ease: E }, { t: 1.4, value: [0.15, 0, 0], ease: E }] },
      { target: "leg-r", pivot: LRP, rotation: [
        { t: 0, value: [-0.15, 0, 0] }, { t: 0.7, value: [0.15, 0, 0], ease: E }, { t: 1.4, value: [-0.15, 0, 0], ease: E }] },
      { target: "head", pivot: HP, position: [
        { t: 0, value: [0, -0.015, 0] }, { t: 0.7, value: [0, -0.012, 0], ease: E }, { t: 1.4, value: [0, -0.015, 0], ease: E }] },
      { target: "arm-l", pivot: ALP, rotation: [
        { t: 0, value: [0.1, 0, -0.15] }, { t: 1.4, value: [0.1, 0, -0.15] }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [0.1, 0, 0.15] }, { t: 1.4, value: [0.1, 0, 0.15] }] },
    ] },
    // ── 5. march ──
    { id: "march", label: "March", duration: 1.0, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0, 0, 0.03] }, { t: 0.5, value: [0, 0, -0.03], ease: E }, { t: 1.0, value: [0, 0, 0.03], ease: E }] },
      { target: "leg-l", pivot: LLP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.25, value: [-0.6, 0, 0], ease: E }, { t: 0.5, value: [0, 0, 0], ease: E }] },
      { target: "leg-r", pivot: LRP, rotation: [
        { t: 0.5, value: [0, 0, 0] }, { t: 0.75, value: [-0.6, 0, 0], ease: E }, { t: 1.0, value: [0, 0, 0], ease: E }] },
      { target: "arm-l", pivot: ALP, rotation: [
        { t: 0, value: [0.3, 0, 0] }, { t: 0.5, value: [-0.3, 0, 0], ease: E }, { t: 1.0, value: [0.3, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [-0.3, 0, 0] }, { t: 0.5, value: [0.3, 0, 0], ease: E }, { t: 1.0, value: [-0.3, 0, 0], ease: E }] },
    ] },
    // ── 6. strafe-left ──
    { id: "strafe-left", label: "Strafe left", duration: 0.6, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [0, 0, 0.08], ease: E }, { t: 0.6, value: [0, 0, 0], ease: E }] },
      { target: "leg-l", pivot: LLP, position: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [-0.03, 0.01, 0], ease: E }, { t: 0.6, value: [0, 0, 0], ease: E }] },
      { target: "leg-r", pivot: LRP, position: [
        { t: 0.15, value: [0, 0, 0] }, { t: 0.45, value: [-0.03, 0.01, 0], ease: E }, { t: 0.6, value: [0, 0, 0], ease: E }] },
      { target: "head", pivot: HP, rotation: [
        { t: 0, value: [0, 0, 0.05] }, { t: 0.6, value: [0, 0, 0.05] }] },
    ] },
    // ── 7. strafe-right ──
    { id: "strafe-right", label: "Strafe right", duration: 0.6, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [0, 0, -0.08], ease: E }, { t: 0.6, value: [0, 0, 0], ease: E }] },
      { target: "leg-l", pivot: LLP, position: [
        { t: 0.15, value: [0, 0, 0] }, { t: 0.45, value: [0.03, 0.01, 0], ease: E }, { t: 0.6, value: [0, 0, 0], ease: E }] },
      { target: "leg-r", pivot: LRP, position: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [0.03, 0.01, 0], ease: E }, { t: 0.6, value: [0, 0, 0], ease: E }] },
      { target: "head", pivot: HP, rotation: [
        { t: 0, value: [0, 0, -0.05] }, { t: 0.6, value: [0, 0, -0.05] }] },
    ] },
    // ── 8. skip ──
    { id: "skip", label: "Skip", duration: 0.7, tracks: [
      { target: "body", pivot: BP, position: [
        { t: 0, value: [0, 0.01, 0] }, { t: 0.175, value: [0, 0, 0], ease: E }, { t: 0.35, value: [0, 0.01, 0], ease: E },
        { t: 0.525, value: [0, 0, 0], ease: E }, { t: 0.7, value: [0, 0.01, 0], ease: E }] },
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0, 0, 0.03] }, { t: 0.35, value: [0, 0, -0.03], ease: E }, { t: 0.7, value: [0, 0, 0.03], ease: E }] },
      { target: "leg-l", pivot: LLP, rotation: [
        { t: 0, value: [0.4, 0, 0] }, { t: 0.35, value: [-0.4, 0, 0], ease: E }, { t: 0.7, value: [0.4, 0, 0], ease: E }] },
      { target: "leg-r", pivot: LRP, rotation: [
        { t: 0, value: [-0.4, 0, 0] }, { t: 0.35, value: [0.4, 0, 0], ease: E }, { t: 0.7, value: [-0.4, 0, 0], ease: E }] },
      { target: "head", pivot: HP, position: [
        { t: 0, value: [0, 0.015, 0] }, { t: 0.175, value: [0, 0, 0], ease: E }, { t: 0.35, value: [0, 0.015, 0], ease: E },
        { t: 0.525, value: [0, 0, 0], ease: E }, { t: 0.7, value: [0, 0.015, 0], ease: E }] },
      { target: "arm-l", pivot: ALP, rotation: [
        { t: 0, value: [0, 0, -0.2] }, { t: 0.35, value: [0, 0, -0.5], ease: E }, { t: 0.7, value: [0, 0, -0.2], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [0, 0, 0.2] }, { t: 0.35, value: [0, 0, 0.5], ease: E }, { t: 0.7, value: [0, 0, 0.2], ease: E }] },
    ] },
    // ── 9. tiptoe ──
    { id: "tiptoe", label: "Tiptoe", duration: 1.2, tracks: [
      { target: "body", pivot: BP, position: [
        { t: 0, value: [0, 0.008, 0] }, { t: 0.6, value: [0, 0.01, 0], ease: E }, { t: 1.2, value: [0, 0.008, 0], ease: E }] },
      { target: "head", pivot: HP, position: [
        { t: 0, value: [0, 0.012, 0] }, { t: 0.6, value: [0, 0.015, 0], ease: E }, { t: 1.2, value: [0, 0.012, 0], ease: E }] },
      { target: "leg-l", pivot: LLP, rotation: [
        { t: 0, value: [0.1, 0, 0] }, { t: 0.6, value: [-0.1, 0, 0], ease: E }, { t: 1.2, value: [0.1, 0, 0], ease: E }] },
      { target: "leg-r", pivot: LRP, rotation: [
        { t: 0, value: [-0.1, 0, 0] }, { t: 0.6, value: [0.1, 0, 0], ease: E }, { t: 1.2, value: [-0.1, 0, 0], ease: E }] },
    ] },
    // ── 10. backward ──
    { id: "backward", label: "Walk backward", duration: 0.9, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [-0.05, 0, 0] }, { t: 0.45, value: [-0.03, 0, 0], ease: E }, { t: 0.9, value: [-0.05, 0, 0], ease: E }] },
      { target: "leg-l", pivot: LLP, rotation: [
        { t: 0, value: [-0.25, 0, 0] }, { t: 0.45, value: [0.25, 0, 0], ease: E }, { t: 0.9, value: [-0.25, 0, 0], ease: E }] },
      { target: "leg-r", pivot: LRP, rotation: [
        { t: 0, value: [0.25, 0, 0] }, { t: 0.45, value: [-0.25, 0, 0], ease: E }, { t: 0.9, value: [0.25, 0, 0], ease: E }] },
      { target: "arm-l", pivot: ALP, rotation: [
        { t: 0, value: [0.2, 0, 0] }, { t: 0.45, value: [-0.2, 0, 0], ease: E }, { t: 0.9, value: [0.2, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [-0.2, 0, 0] }, { t: 0.45, value: [0.2, 0, 0], ease: E }, { t: 0.9, value: [-0.2, 0, 0], ease: E }] },
    ] },
    // ── 11. attack-slash ──
    { id: "attack-slash", label: "Slash attack", duration: 0.8, loop: false, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.2, value: [0, 0.2, 0], ease: E },
        { t: 0.4, value: [0.1, -0.3, 0], ease: "easeOut" as const }, { t: 0.8, value: [0, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.2, value: [-1.2, 0, 0.3], ease: E },
        { t: 0.4, value: [0.8, 0, -0.2], ease: "easeOut" as const }, { t: 0.8, value: [0, 0, 0], ease: E }] },
      { target: "head", pivot: HP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [0, -0.2, 0], ease: E }, { t: 0.8, value: [0, 0, 0], ease: E }] },
    ] },
    // ── 12. attack-thrust ──
    { id: "attack-thrust", label: "Thrust attack", duration: 0.7, loop: false, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.15, value: [-0.08, 0, 0], ease: E },
        { t: 0.35, value: [0.15, 0, 0], ease: "easeOut" as const }, { t: 0.7, value: [0, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.15, value: [-0.5, 0, 0.2], ease: E },
        { t: 0.35, value: [1.0, 0, 0], ease: "easeOut" as const }, { t: 0.7, value: [0, 0, 0], ease: E }] },
      { target: "arm-l", pivot: ALP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.35, value: [0.3, 0, -0.1], ease: E }, { t: 0.7, value: [0, 0, 0], ease: E }] },
    ] },
    // ── 13. attack-overhead ──
    { id: "attack-overhead", label: "Overhead smash", duration: 1.0, loop: false, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.35, value: [-0.15, 0, 0], ease: E },
        { t: 0.55, value: [0.2, 0, 0], ease: "easeOut" as const }, { t: 1.0, value: [0, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.35, value: [-2.2, 0, 0], ease: E },
        { t: 0.55, value: [0.6, 0, 0], ease: "easeOut" as const }, { t: 1.0, value: [0, 0, 0], ease: E }] },
      { target: "arm-l", pivot: ALP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.35, value: [-2.2, 0, 0], ease: E },
        { t: 0.55, value: [0.6, 0, 0], ease: "easeOut" as const }, { t: 1.0, value: [0, 0, 0], ease: E }] },
      { target: "head", pivot: HP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.35, value: [-0.15, 0, 0], ease: E },
        { t: 0.55, value: [0.1, 0, 0], ease: E }, { t: 1.0, value: [0, 0, 0], ease: E }] },
    ] },
    // ── 14. block ──
    { id: "block", label: "Block", duration: 1.2, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [-0.08, 0, 0], ease: E }, { t: 0.9, value: [-0.08, 0, 0] },
        { t: 1.2, value: [0, 0, 0], ease: E }] },
      { target: "arm-l", pivot: ALP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [-1.0, 0.5, 0], ease: E }, { t: 0.9, value: [-1.0, 0.5, 0] },
        { t: 1.2, value: [0, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [-0.8, -0.3, 0], ease: E }, { t: 0.9, value: [-0.8, -0.3, 0] },
        { t: 1.2, value: [0, 0, 0], ease: E }] },
      { target: "head", pivot: HP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [0.1, 0, 0], ease: E }, { t: 0.9, value: [0.1, 0, 0] },
        { t: 1.2, value: [0, 0, 0], ease: E }] },
    ] },
    // ── 15. parry ──
    { id: "parry", label: "Parry", duration: 0.6, loop: false, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.15, value: [0, 0.12, 0], ease: E },
        { t: 0.35, value: [0, -0.08, 0], ease: "easeOut" as const }, { t: 0.6, value: [0, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.15, value: [-0.8, 0.6, 0.3], ease: E },
        { t: 0.35, value: [-0.3, -0.4, -0.2], ease: "easeOut" as const }, { t: 0.6, value: [0, 0, 0], ease: E }] },
      { target: "head", pivot: HP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.15, value: [0, 0.15, 0], ease: E }, { t: 0.6, value: [0, 0, 0], ease: E }] },
    ] },
    // ── 16. dodge-left ──
    { id: "dodge-left", label: "Dodge left", duration: 0.5, loop: false, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.15, value: [0, 0, 0.15], ease: E },
        { t: 0.35, value: [0, 0, 0.15] }, { t: 0.5, value: [0, 0, 0], ease: E }] },
      { target: "body", pivot: BP, position: [
        { t: 0, value: [0, 0, 0] }, { t: 0.15, value: [-0.04, 0, 0], ease: E },
        { t: 0.35, value: [-0.04, 0, 0] }, { t: 0.5, value: [0, 0, 0], ease: E }] },
      { target: "head", pivot: HP, position: [
        { t: 0, value: [0, 0, 0] }, { t: 0.15, value: [-0.06, 0.01, 0], ease: E },
        { t: 0.35, value: [-0.06, 0.01, 0] }, { t: 0.5, value: [0, 0, 0], ease: E }] },
      { target: "leg-l", pivot: LLP, position: [
        { t: 0, value: [0, 0, 0] }, { t: 0.15, value: [-0.04, 0, 0], ease: E }, { t: 0.5, value: [0, 0, 0], ease: E }] },
      { target: "leg-r", pivot: LRP, position: [
        { t: 0, value: [0, 0, 0] }, { t: 0.2, value: [-0.04, 0, 0], ease: E }, { t: 0.5, value: [0, 0, 0], ease: E }] },
    ] },
    // ── 17. dodge-right ──
    { id: "dodge-right", label: "Dodge right", duration: 0.5, loop: false, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.15, value: [0, 0, -0.15], ease: E },
        { t: 0.35, value: [0, 0, -0.15] }, { t: 0.5, value: [0, 0, 0], ease: E }] },
      { target: "body", pivot: BP, position: [
        { t: 0, value: [0, 0, 0] }, { t: 0.15, value: [0.04, 0, 0], ease: E },
        { t: 0.35, value: [0.04, 0, 0] }, { t: 0.5, value: [0, 0, 0], ease: E }] },
      { target: "head", pivot: HP, position: [
        { t: 0, value: [0, 0, 0] }, { t: 0.15, value: [0.06, 0.01, 0], ease: E },
        { t: 0.35, value: [0.06, 0.01, 0] }, { t: 0.5, value: [0, 0, 0], ease: E }] },
      { target: "leg-l", pivot: LLP, position: [
        { t: 0, value: [0, 0, 0] }, { t: 0.2, value: [0.04, 0, 0], ease: E }, { t: 0.5, value: [0, 0, 0], ease: E }] },
      { target: "leg-r", pivot: LRP, position: [
        { t: 0, value: [0, 0, 0] }, { t: 0.15, value: [0.04, 0, 0], ease: E }, { t: 0.5, value: [0, 0, 0], ease: E }] },
    ] },
    // ── 18. charge ──
    { id: "charge", label: "Charge", duration: 0.6, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0.12, 0, 0] }, { t: 0.3, value: [0.15, 0, 0], ease: E }, { t: 0.6, value: [0.12, 0, 0], ease: E }] },
      { target: "head", pivot: HP, rotation: [
        { t: 0, value: [0.15, 0, 0] }, { t: 0.3, value: [0.2, 0, 0], ease: E }, { t: 0.6, value: [0.15, 0, 0], ease: E }] },
      { target: "arm-l", pivot: ALP, rotation: [
        { t: 0, value: [-0.5, 0, 0] }, { t: 0.3, value: [0.5, 0, 0], ease: E }, { t: 0.6, value: [-0.5, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [0.5, 0, 0] }, { t: 0.3, value: [-0.5, 0, 0], ease: E }, { t: 0.6, value: [0.5, 0, 0], ease: E }] },
      { target: "leg-l", pivot: LLP, rotation: [
        { t: 0, value: [0.6, 0, 0] }, { t: 0.3, value: [-0.6, 0, 0], ease: E }, { t: 0.6, value: [0.6, 0, 0], ease: E }] },
      { target: "leg-r", pivot: LRP, rotation: [
        { t: 0, value: [-0.6, 0, 0] }, { t: 0.3, value: [0.6, 0, 0], ease: E }, { t: 0.6, value: [-0.6, 0, 0], ease: E }] },
    ] },
    // ── 19. kick ──
    { id: "kick", label: "Kick", duration: 0.7, loop: false, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.2, value: [-0.06, 0, 0], ease: E },
        { t: 0.35, value: [0.1, 0, 0], ease: "easeOut" as const }, { t: 0.7, value: [0, 0, 0], ease: E }] },
      { target: "leg-r", pivot: LRP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.2, value: [0.3, 0, 0], ease: E },
        { t: 0.35, value: [-1.0, 0, 0], ease: "easeOut" as const }, { t: 0.7, value: [0, 0, 0], ease: E }] },
      { target: "arm-l", pivot: ALP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.35, value: [-0.4, 0, -0.2], ease: E }, { t: 0.7, value: [0, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.35, value: [0.3, 0, 0.2], ease: E }, { t: 0.7, value: [0, 0, 0], ease: E }] },
    ] },
    // ── 20. headbutt ──
    { id: "headbutt", label: "Headbutt", duration: 0.6, loop: false, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.15, value: [-0.1, 0, 0], ease: E },
        { t: 0.3, value: [0.15, 0, 0], ease: "easeOut" as const }, { t: 0.6, value: [0, 0, 0], ease: E }] },
      { target: "head", pivot: HP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.15, value: [-0.3, 0, 0], ease: E },
        { t: 0.3, value: [0.5, 0, 0], ease: "easeOut" as const }, { t: 0.6, value: [0, 0, 0], ease: E }] },
      { target: "head", pivot: HP, position: [
        { t: 0, value: [0, 0, 0] }, { t: 0.15, value: [0, 0, -0.01] },
        { t: 0.3, value: [0, 0, 0.03], ease: "easeOut" as const }, { t: 0.6, value: [0, 0, 0], ease: E }] },
    ] },
    // ── 21. wave ──
    { id: "wave", label: "Wave", duration: 1.2, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.2, value: [0, 0, -0.05], ease: E },
        { t: 1.0, value: [0, 0, -0.05] }, { t: 1.2, value: [0, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.2, value: [-2.5, 0, 0.3], ease: E },
        { t: 0.5, value: [-2.5, 0, 0.6], ease: E }, { t: 0.8, value: [-2.5, 0, 0.3], ease: E },
        { t: 1.0, value: [-2.5, 0, 0.6], ease: E }, { t: 1.2, value: [0, 0, 0], ease: E }] },
    ] },
    // ── 22. salute ──
    { id: "salute", label: "Salute", duration: 1.5, loop: false, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [0.03, 0, 0], ease: E },
        { t: 1.1, value: [0.03, 0, 0] }, { t: 1.5, value: [0, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [-1.8, 0.3, 0], ease: E },
        { t: 1.1, value: [-1.8, 0.3, 0] }, { t: 1.5, value: [0, 0, 0], ease: E }] },
      { target: "head", pivot: HP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [0.05, 0, 0], ease: E },
        { t: 1.1, value: [0.05, 0, 0] }, { t: 1.5, value: [0, 0, 0], ease: E }] },
    ] },
    // ── 23. bow ──
    { id: "bow", label: "Bow", duration: 1.6, loop: false, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.4, value: [0.35, 0, 0], ease: E },
        { t: 1.1, value: [0.35, 0, 0] }, { t: 1.6, value: [0, 0, 0], ease: E }] },
      { target: "head", pivot: HP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.4, value: [0.6, 0, 0], ease: E },
        { t: 1.1, value: [0.6, 0, 0] }, { t: 1.6, value: [0, 0, 0], ease: E }] },
      { target: "arm-l", pivot: ALP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.4, value: [0.3, 0, 0.1], ease: E },
        { t: 1.1, value: [0.3, 0, 0.1] }, { t: 1.6, value: [0, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.4, value: [0.3, 0, -0.1], ease: E },
        { t: 1.1, value: [0.3, 0, -0.1] }, { t: 1.6, value: [0, 0, 0], ease: E }] },
    ] },
    // ── 24. nod ──
    { id: "nod", label: "Nod", duration: 0.8, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.2, value: [0.04, 0, 0], ease: E },
        { t: 0.4, value: [0, 0, 0], ease: E }, { t: 0.6, value: [0.04, 0, 0], ease: E },
        { t: 0.8, value: [0, 0, 0], ease: E }] },
      { target: "head", pivot: HP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.2, value: [0.2, 0, 0], ease: E },
        { t: 0.4, value: [0, 0, 0], ease: E }, { t: 0.6, value: [0.2, 0, 0], ease: E },
        { t: 0.8, value: [0, 0, 0], ease: E }] },
    ] },
    // ── 25. shake-head ──
    { id: "shake-head", label: "Shake head", duration: 0.8, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.2, value: [0, 0.04, 0], ease: E },
        { t: 0.4, value: [0, -0.04, 0], ease: E }, { t: 0.6, value: [0, 0.04, 0], ease: E },
        { t: 0.8, value: [0, 0, 0], ease: E }] },
      { target: "head", pivot: HP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.2, value: [0, 0.25, 0], ease: E },
        { t: 0.4, value: [0, -0.25, 0], ease: E }, { t: 0.6, value: [0, 0.25, 0], ease: E },
        { t: 0.8, value: [0, 0, 0], ease: E }] },
    ] },
    // ── 26. clap ──
    { id: "clap", label: "Clap", duration: 0.6, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0.04, 0, 0] }, { t: 0.15, value: [0.06, 0, 0], ease: E },
        { t: 0.3, value: [0.04, 0, 0], ease: E }, { t: 0.45, value: [0.06, 0, 0], ease: E },
        { t: 0.6, value: [0.04, 0, 0], ease: E }] },
      { target: "arm-l", pivot: ALP, rotation: [
        { t: 0, value: [-1.2, 0, 0.6] }, { t: 0.15, value: [-1.2, 0, 0], ease: E },
        { t: 0.3, value: [-1.2, 0, 0.6], ease: E }, { t: 0.45, value: [-1.2, 0, 0], ease: E },
        { t: 0.6, value: [-1.2, 0, 0.6], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [-1.2, 0, -0.6] }, { t: 0.15, value: [-1.2, 0, 0], ease: E },
        { t: 0.3, value: [-1.2, 0, -0.6], ease: E }, { t: 0.45, value: [-1.2, 0, 0], ease: E },
        { t: 0.6, value: [-1.2, 0, -0.6], ease: E }] },
    ] },
    // ── 27. point ──
    { id: "point", label: "Point", duration: 1.4, loop: false, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [0, -0.1, 0], ease: E },
        { t: 1.0, value: [0, -0.1, 0] }, { t: 1.4, value: [0, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [-1.4, 0, 0], ease: E },
        { t: 1.0, value: [-1.4, 0, 0] }, { t: 1.4, value: [0, 0, 0], ease: E }] },
      { target: "head", pivot: HP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [-0.1, -0.1, 0], ease: E },
        { t: 1.0, value: [-0.1, -0.1, 0] }, { t: 1.4, value: [0, 0, 0], ease: E }] },
    ] },
    // ── 28. beckon ──
    { id: "beckon", label: "Beckon", duration: 1.0, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0.05, 0, 0] }, { t: 0.25, value: [0.03, 0, 0], ease: E },
        { t: 0.5, value: [0.05, 0, 0], ease: E }, { t: 0.75, value: [0.03, 0, 0], ease: E },
        { t: 1.0, value: [0.05, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [-1.3, 0, 0] }, { t: 0.25, value: [-0.8, 0, 0], ease: E },
        { t: 0.5, value: [-1.3, 0, 0], ease: E }, { t: 0.75, value: [-0.8, 0, 0], ease: E },
        { t: 1.0, value: [-1.3, 0, 0], ease: E }] },
    ] },
    // ── 29. shrug ──
    { id: "shrug", label: "Shrug", duration: 1.2, loop: false, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [0, 0, 0.04], ease: E },
        { t: 0.8, value: [0, 0, 0.04] }, { t: 1.2, value: [0, 0, 0], ease: E }] },
      { target: "arm-l", pivot: ALP, position: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [0, 0.025, 0], ease: E },
        { t: 0.8, value: [0, 0.025, 0] }, { t: 1.2, value: [0, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, position: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [0, 0.025, 0], ease: E },
        { t: 0.8, value: [0, 0.025, 0] }, { t: 1.2, value: [0, 0, 0], ease: E }] },
      { target: "arm-l", pivot: ALP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [0, 0, -0.4], ease: E },
        { t: 0.8, value: [0, 0, -0.4] }, { t: 1.2, value: [0, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [0, 0, 0.4], ease: E },
        { t: 0.8, value: [0, 0, 0.4] }, { t: 1.2, value: [0, 0, 0], ease: E }] },
      { target: "head", pivot: HP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [0, 0, 0.08], ease: E },
        { t: 0.8, value: [0, 0, 0.08] }, { t: 1.2, value: [0, 0, 0], ease: E }] },
    ] },
    // ── 30. flex ──
    { id: "flex", label: "Flex", duration: 1.6, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [-0.08, 0, 0], ease: E },
        { t: 0.8, value: [-0.1, 0, 0], ease: E }, { t: 1.2, value: [-0.08, 0, 0], ease: E },
        { t: 1.6, value: [0, 0, 0], ease: E }] },
      { target: "arm-l", pivot: ALP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [-1.8, 0, -0.5], ease: E },
        { t: 0.8, value: [-1.8, 0, -0.8], ease: E }, { t: 1.2, value: [-1.8, 0, -0.5], ease: E },
        { t: 1.6, value: [0, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [-1.8, 0, 0.5], ease: E },
        { t: 0.8, value: [-1.8, 0, 0.8], ease: E }, { t: 1.2, value: [-1.8, 0, 0.5], ease: E },
        { t: 1.6, value: [0, 0, 0], ease: E }] },
    ] },
    // ── 31. jump ──
    { id: "jump", label: "Jump", duration: 0.8, loop: false, tracks: [
      { target: "body", pivot: BP, position: [
        { t: 0, value: [0, 0, 0] }, { t: 0.2, value: [0, -0.008, 0], ease: E },
        { t: 0.5, value: [0, 0.035, 0], ease: E }, { t: 0.8, value: [0, 0, 0], ease: E }] },
      { target: "head", pivot: HP, position: [
        { t: 0, value: [0, 0, 0] }, { t: 0.2, value: [0, -0.01, 0], ease: E },
        { t: 0.5, value: [0, 0.04, 0], ease: E }, { t: 0.8, value: [0, 0, 0], ease: E }] },
      { target: "leg-l", pivot: LLP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.2, value: [0.2, 0, 0], ease: E },
        { t: 0.5, value: [-0.3, 0, 0], ease: E }, { t: 0.8, value: [0, 0, 0], ease: E }] },
      { target: "leg-r", pivot: LRP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.2, value: [0.2, 0, 0], ease: E },
        { t: 0.5, value: [-0.3, 0, 0], ease: E }, { t: 0.8, value: [0, 0, 0], ease: E }] },
      { target: "arm-l", pivot: ALP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [0, 0, -0.8], ease: E }, { t: 0.8, value: [0, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [0, 0, 0.8], ease: E }, { t: 0.8, value: [0, 0, 0], ease: E }] },
    ] },
    // ── 32. land ──
    { id: "land", label: "Land", duration: 0.5, loop: false, tracks: [
      { target: "body", pivot: BP, position: [
        { t: 0, value: [0, 0.015, 0] }, { t: 0.15, value: [0, -0.01, 0], ease: "easeOut" as const },
        { t: 0.5, value: [0, 0, 0], ease: E }] },
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.15, value: [0.08, 0, 0], ease: E }, { t: 0.5, value: [0, 0, 0], ease: E }] },
      { target: "head", pivot: HP, position: [
        { t: 0, value: [0, 0.02, 0] }, { t: 0.15, value: [0, -0.015, 0], ease: "easeOut" as const },
        { t: 0.5, value: [0, 0, 0], ease: E }] },
      { target: "leg-l", pivot: LLP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.15, value: [0.2, 0, 0], ease: E }, { t: 0.5, value: [0, 0, 0], ease: E }] },
      { target: "leg-r", pivot: LRP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.15, value: [0.2, 0, 0], ease: E }, { t: 0.5, value: [0, 0, 0], ease: E }] },
    ] },
    // ── 33. pickup ──
    { id: "pickup", label: "Pick up", duration: 1.0, loop: false, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [0.25, 0, 0], ease: E },
        { t: 0.7, value: [0.25, 0, 0] }, { t: 1.0, value: [0, 0, 0], ease: E }] },
      { target: "head", pivot: HP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [0.4, 0, 0], ease: E },
        { t: 0.7, value: [0.4, 0, 0] }, { t: 1.0, value: [0, 0, 0], ease: E }] },
      { target: "arm-l", pivot: ALP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [0.8, 0, 0.2], ease: E },
        { t: 0.7, value: [0.8, 0, 0.2] }, { t: 1.0, value: [0, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [0.8, 0, -0.2], ease: E },
        { t: 0.7, value: [0.8, 0, -0.2] }, { t: 1.0, value: [0, 0, 0], ease: E }] },
    ] },
    // ── 34. throw ──
    { id: "throw", label: "Throw", duration: 0.8, loop: false, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.25, value: [-0.1, 0.15, 0], ease: E },
        { t: 0.45, value: [0.12, -0.2, 0], ease: "easeOut" as const }, { t: 0.8, value: [0, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.25, value: [-2.0, 0, 0.3], ease: E },
        { t: 0.45, value: [0.5, 0, -0.1], ease: "easeOut" as const }, { t: 0.8, value: [0, 0, 0], ease: E }] },
      { target: "head", pivot: HP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.25, value: [-0.1, 0, 0], ease: E },
        { t: 0.45, value: [0.15, 0, 0], ease: E }, { t: 0.8, value: [0, 0, 0], ease: E }] },
    ] },
    // ── 35. push ──
    { id: "push", label: "Push", duration: 0.9, loop: false, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.25, value: [-0.05, 0, 0], ease: E },
        { t: 0.5, value: [0.15, 0, 0], ease: "easeOut" as const }, { t: 0.9, value: [0, 0, 0], ease: E }] },
      { target: "arm-l", pivot: ALP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.25, value: [-1.2, 0, 0.3], ease: E },
        { t: 0.5, value: [-1.4, 0, 0.1], ease: "easeOut" as const }, { t: 0.9, value: [0, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.25, value: [-1.2, 0, -0.3], ease: E },
        { t: 0.5, value: [-1.4, 0, -0.1], ease: "easeOut" as const }, { t: 0.9, value: [0, 0, 0], ease: E }] },
      { target: "head", pivot: HP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.4, value: [0.15, 0, 0], ease: E }, { t: 0.9, value: [0, 0, 0], ease: E }] },
    ] },
    // ── 36. pull ──
    { id: "pull", label: "Pull", duration: 1.0, loop: false, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [-0.1, 0, 0] }, { t: 0.4, value: [0.08, 0, 0], ease: E }, { t: 1.0, value: [0, 0, 0], ease: E }] },
      { target: "arm-l", pivot: ALP, rotation: [
        { t: 0, value: [-1.3, 0, 0.2] }, { t: 0.4, value: [-0.3, 0, 0], ease: E }, { t: 1.0, value: [0, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [-1.3, 0, -0.2] }, { t: 0.4, value: [-0.3, 0, 0], ease: E }, { t: 1.0, value: [0, 0, 0], ease: E }] },
      { target: "head", pivot: HP, rotation: [
        { t: 0, value: [-0.1, 0, 0] }, { t: 0.4, value: [0.1, 0, 0], ease: E }, { t: 1.0, value: [0, 0, 0], ease: E }] },
    ] },
    // ── 37. climb ──
    { id: "climb", label: "Climb", duration: 1.0, tracks: [
      { target: "body", pivot: BP, position: [
        { t: 0, value: [0, 0, 0] }, { t: 0.5, value: [0, 0.008, 0], ease: E }, { t: 1.0, value: [0, 0, 0], ease: E }] },
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0, 0, 0.04] }, { t: 0.5, value: [0, 0, -0.04], ease: E }, { t: 1.0, value: [0, 0, 0.04], ease: E }] },
      { target: "arm-l", pivot: ALP, rotation: [
        { t: 0, value: [-2.2, 0, 0] }, { t: 0.5, value: [-0.5, 0, 0], ease: E }, { t: 1.0, value: [-2.2, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [-0.5, 0, 0] }, { t: 0.5, value: [-2.2, 0, 0], ease: E }, { t: 1.0, value: [-0.5, 0, 0], ease: E }] },
      { target: "leg-l", pivot: LLP, rotation: [
        { t: 0, value: [-0.4, 0, 0] }, { t: 0.5, value: [0.3, 0, 0], ease: E }, { t: 1.0, value: [-0.4, 0, 0], ease: E }] },
      { target: "leg-r", pivot: LRP, rotation: [
        { t: 0, value: [0.3, 0, 0] }, { t: 0.5, value: [-0.4, 0, 0], ease: E }, { t: 1.0, value: [0.3, 0, 0], ease: E }] },
    ] },
    // ── 38. dig ──
    { id: "dig", label: "Dig", duration: 1.2, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [-0.08, 0, 0], ease: E },
        { t: 0.6, value: [0.12, 0, 0], ease: "easeOut" as const }, { t: 1.2, value: [0, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [-1.6, 0, 0], ease: E },
        { t: 0.6, value: [0.5, 0, 0], ease: "easeOut" as const }, { t: 1.2, value: [0, 0, 0], ease: E }] },
      { target: "arm-l", pivot: ALP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [-1.6, 0, 0], ease: E },
        { t: 0.6, value: [0.5, 0, 0], ease: "easeOut" as const }, { t: 1.2, value: [0, 0, 0], ease: E }] },
      { target: "head", pivot: HP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [-0.1, 0, 0], ease: E },
        { t: 0.6, value: [0.2, 0, 0], ease: E }, { t: 1.2, value: [0, 0, 0], ease: E }] },
    ] },
    // ── 39. chop ──
    { id: "chop", label: "Chop", duration: 1.0, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.35, value: [0, 0.1, -0.05], ease: E },
        { t: 0.55, value: [0.1, -0.15, 0.03], ease: "easeOut" as const }, { t: 1.0, value: [0, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.35, value: [-2.0, -0.3, 0], ease: E },
        { t: 0.55, value: [0.3, 0.2, 0], ease: "easeOut" as const }, { t: 1.0, value: [0, 0, 0], ease: E }] },
      { target: "head", pivot: HP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.35, value: [0, 0.15, 0], ease: E },
        { t: 0.55, value: [0, -0.1, 0], ease: E }, { t: 1.0, value: [0, 0, 0], ease: E }] },
    ] },
    // ── 40. mine ──
    { id: "mine", label: "Mine", duration: 1.1, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.35, value: [-0.06, 0, 0.03], ease: E },
        { t: 0.55, value: [0.1, 0, -0.02], ease: "easeOut" as const }, { t: 1.1, value: [0, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.35, value: [-1.8, 0, 0.2], ease: E },
        { t: 0.55, value: [0.4, 0, -0.1], ease: "easeOut" as const }, { t: 1.1, value: [0, 0, 0], ease: E }] },
      { target: "arm-l", pivot: ALP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.55, value: [-0.3, 0, 0], ease: E }, { t: 1.1, value: [0, 0, 0], ease: E }] },
      { target: "leg-l", pivot: LLP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.55, value: [0.15, 0, 0], ease: E }, { t: 1.1, value: [0, 0, 0], ease: E }] },
    ] },
    // ── 41. cheer ──
    { id: "cheer", label: "Cheer", duration: 1.0, tracks: [
      { target: "body", pivot: BP, position: [
        { t: 0, value: [0, 0, 0] }, { t: 0.25, value: [0, 0.01, 0], ease: E },
        { t: 0.5, value: [0, 0, 0], ease: E }, { t: 0.75, value: [0, 0.01, 0], ease: E },
        { t: 1.0, value: [0, 0, 0], ease: E }] },
      { target: "arm-l", pivot: ALP, rotation: [
        { t: 0, value: [0, 0, -0.3] }, { t: 0.25, value: [-2.8, 0, -0.3], ease: E },
        { t: 0.5, value: [-2.4, 0, -0.3], ease: E }, { t: 0.75, value: [-2.8, 0, -0.3], ease: E },
        { t: 1.0, value: [0, 0, -0.3], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [0, 0, 0.3] }, { t: 0.25, value: [-2.8, 0, 0.3], ease: E },
        { t: 0.5, value: [-2.4, 0, 0.3], ease: E }, { t: 0.75, value: [-2.8, 0, 0.3], ease: E },
        { t: 1.0, value: [0, 0, 0.3], ease: E }] },
      { target: "head", pivot: HP, position: [
        { t: 0, value: [0, 0, 0] }, { t: 0.25, value: [0, 0.01, 0], ease: E },
        { t: 0.5, value: [0, 0, 0], ease: E }, { t: 0.75, value: [0, 0.01, 0], ease: E },
        { t: 1.0, value: [0, 0, 0], ease: E }] },
    ] },
    // ── 42. taunt ──
    { id: "taunt", label: "Taunt", duration: 1.4, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [0, 0, 0.06], ease: E },
        { t: 0.7, value: [0, 0, -0.06], ease: E }, { t: 1.1, value: [0, 0, 0.06], ease: E },
        { t: 1.4, value: [0, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [-1.5, 0, 0], ease: E },
        { t: 0.5, value: [-1.5, 0.4, 0], ease: E }, { t: 0.7, value: [-1.5, -0.4, 0], ease: E },
        { t: 0.9, value: [-1.5, 0.4, 0], ease: E }, { t: 1.1, value: [-1.5, 0, 0], ease: E },
        { t: 1.4, value: [0, 0, 0], ease: E }] },
      { target: "head", pivot: HP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [0, 0, 0.1], ease: E },
        { t: 1.1, value: [0, 0, 0.1] }, { t: 1.4, value: [0, 0, 0], ease: E }] },
    ] },
    // ── 43. dance ──
    { id: "dance", label: "Dance", duration: 1.2, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [0, 0.1, 0.06], ease: E },
        { t: 0.6, value: [0, -0.1, -0.06], ease: E }, { t: 0.9, value: [0, 0.1, 0.06], ease: E },
        { t: 1.2, value: [0, 0, 0], ease: E }] },
      { target: "body", pivot: BP, position: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [0, 0.008, 0], ease: E },
        { t: 0.6, value: [0, 0, 0], ease: E }, { t: 0.9, value: [0, 0.008, 0], ease: E },
        { t: 1.2, value: [0, 0, 0], ease: E }] },
      { target: "leg-l", pivot: LLP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [-0.4, 0.2, 0], ease: E },
        { t: 0.6, value: [0, 0, 0], ease: E }, { t: 0.9, value: [0.2, -0.2, 0], ease: E },
        { t: 1.2, value: [0, 0, 0], ease: E }] },
      { target: "leg-r", pivot: LRP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [0.2, -0.2, 0], ease: E },
        { t: 0.6, value: [0, 0, 0], ease: E }, { t: 0.9, value: [-0.4, 0.2, 0], ease: E },
        { t: 1.2, value: [0, 0, 0], ease: E }] },
      { target: "arm-l", pivot: ALP, rotation: [
        { t: 0, value: [0, 0, -0.5] }, { t: 0.3, value: [-1.5, 0, -0.8], ease: E },
        { t: 0.6, value: [0, 0, -0.5], ease: E }, { t: 0.9, value: [-1.5, 0, -0.8], ease: E },
        { t: 1.2, value: [0, 0, -0.5], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [0, 0, 0.5] }, { t: 0.3, value: [-1.5, 0, 0.8], ease: E },
        { t: 0.6, value: [0, 0, 0.5], ease: E }, { t: 0.9, value: [-1.5, 0, 0.8], ease: E },
        { t: 1.2, value: [0, 0, 0.5], ease: E }] },
      { target: "head", pivot: HP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [0, 0, 0.1], ease: E },
        { t: 0.6, value: [0, 0, -0.1], ease: E }, { t: 0.9, value: [0, 0, 0.1], ease: E },
        { t: 1.2, value: [0, 0, 0], ease: E }] },
    ] },
    // ── 44. celebrate ──
    { id: "celebrate", label: "Celebrate", duration: 1.6, loop: false, tracks: [
      { target: "body", pivot: BP, position: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [0, 0.015, 0], ease: E },
        { t: 0.6, value: [0, 0.005, 0], ease: E }, { t: 0.9, value: [0, 0.015, 0], ease: E },
        { t: 1.2, value: [0, 0.005, 0], ease: E }, { t: 1.6, value: [0, 0, 0], ease: E }] },
      { target: "head", pivot: HP, position: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [0, 0.03, 0], ease: E },
        { t: 0.6, value: [0, 0.01, 0], ease: E }, { t: 0.9, value: [0, 0.03, 0], ease: E },
        { t: 1.2, value: [0, 0.01, 0], ease: E }, { t: 1.6, value: [0, 0, 0], ease: E }] },
      { target: "arm-l", pivot: ALP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [-2.8, 0, -0.4], ease: E },
        { t: 1.2, value: [-2.8, 0, -0.4] }, { t: 1.6, value: [0, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [-2.8, 0, 0.4], ease: E },
        { t: 1.2, value: [-2.8, 0, 0.4] }, { t: 1.6, value: [0, 0, 0], ease: E }] },
    ] },
    // ── 45. dizzy ──
    { id: "dizzy", label: "Dizzy", duration: 1.5, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0, 0, -0.08] }, { t: 0.375, value: [0.05, 0, 0.08], ease: E },
        { t: 0.75, value: [0, 0, -0.08], ease: E }, { t: 1.125, value: [-0.05, 0, 0.08], ease: E },
        { t: 1.5, value: [0, 0, -0.08], ease: E }] },
      { target: "head", pivot: HP, rotation: [
        { t: 0, value: [0, 0, -0.15] }, { t: 0.375, value: [0.1, 0, 0.15], ease: E },
        { t: 0.75, value: [0, 0, -0.15], ease: E }, { t: 1.125, value: [-0.1, 0, 0.15], ease: E },
        { t: 1.5, value: [0, 0, -0.15], ease: E }] },
      { target: "arm-l", pivot: ALP, rotation: [
        { t: 0, value: [0, 0, -0.3] }, { t: 0.75, value: [0.2, 0, -0.1], ease: E },
        { t: 1.5, value: [0, 0, -0.3], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [0.2, 0, 0.1] }, { t: 0.75, value: [0, 0, 0.3], ease: E },
        { t: 1.5, value: [0.2, 0, 0.1], ease: E }] },
    ] },
    // ── 46. hurt ──
    { id: "hurt", label: "Hurt", duration: 0.6, loop: false, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.1, value: [-0.12, 0, 0.08], ease: "easeOut" as const },
        { t: 0.3, value: [0.06, 0, -0.03], ease: E }, { t: 0.6, value: [0, 0, 0], ease: E }] },
      { target: "body", pivot: BP, position: [
        { t: 0, value: [0, 0, 0] }, { t: 0.1, value: [0, 0, -0.012] },
        { t: 0.6, value: [0, 0, 0], ease: E }] },
      { target: "head", pivot: HP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.1, value: [-0.2, 0, 0.1], ease: "easeOut" as const },
        { t: 0.3, value: [0.15, 0, -0.05], ease: E }, { t: 0.6, value: [0, 0, 0], ease: E }] },
      { target: "arm-l", pivot: ALP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.1, value: [0.3, 0, -0.3], ease: E }, { t: 0.6, value: [0, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.1, value: [0.3, 0, 0.3], ease: E }, { t: 0.6, value: [0, 0, 0], ease: E }] },
    ] },
    // ── 47. death ──
    { id: "death", label: "Death", duration: 1.5, loop: false, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.5, value: [0.15, 0, 0.1], ease: E },
        { t: 1.5, value: [0.5, 0, 0.3], ease: E }] },
      { target: "body", pivot: BP, position: [
        { t: 0, value: [0, 0, 0] }, { t: 0.5, value: [0, -0.02, 0], ease: E },
        { t: 1.5, value: [0.01, -0.06, 0.01], ease: E }] },
      { target: "head", pivot: HP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.5, value: [0.3, 0, 0], ease: E },
        { t: 1.5, value: [0.8, 0, 0.3], ease: E }] },
      { target: "head", pivot: HP, position: [
        { t: 0, value: [0, 0, 0] }, { t: 0.5, value: [0, -0.03, 0], ease: E },
        { t: 1.5, value: [0.02, -0.12, 0.02], ease: E }] },
      { target: "arm-l", pivot: ALP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.5, value: [0.2, 0, -0.3], ease: E },
        { t: 1.5, value: [0.5, 0, -0.6], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.5, value: [0.2, 0, 0.3], ease: E },
        { t: 1.5, value: [0.5, 0, 0.6], ease: E }] },
      { target: "leg-l", pivot: LLP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 1.5, value: [0.3, 0, 0], ease: E }] },
      { target: "leg-r", pivot: LRP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 1.5, value: [0.3, 0, 0], ease: E }] },
    ] },
    // ── 48. revive ──
    { id: "revive", label: "Revive", duration: 1.5, loop: false, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0.5, 0, 0.3] }, { t: 0.7, value: [0.1, 0, 0.05], ease: E },
        { t: 1.5, value: [0, 0, 0], ease: E }] },
      { target: "body", pivot: BP, position: [
        { t: 0, value: [0.01, -0.06, 0.01] }, { t: 0.7, value: [0, -0.01, 0], ease: E },
        { t: 1.5, value: [0, 0, 0], ease: E }] },
      { target: "head", pivot: HP, position: [
        { t: 0, value: [0.02, -0.12, 0.02] }, { t: 0.7, value: [0, -0.03, 0], ease: E },
        { t: 1.5, value: [0, 0, 0], ease: E }] },
      { target: "head", pivot: HP, rotation: [
        { t: 0, value: [0.8, 0, 0.3] }, { t: 0.7, value: [0.2, 0, 0], ease: E },
        { t: 1.5, value: [0, 0, 0], ease: E }] },
      { target: "arm-l", pivot: ALP, rotation: [
        { t: 0, value: [0.5, 0, -0.6] }, { t: 1.5, value: [0, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [0.5, 0, 0.6] }, { t: 1.5, value: [0, 0, 0], ease: E }] },
    ] },
    // ── 49. pray ──
    { id: "pray", label: "Pray", duration: 2.5, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0.06, 0, 0] }, { t: 1.25, value: [0.08, 0, 0], ease: E },
        { t: 2.5, value: [0.06, 0, 0], ease: E }] },
      { target: "arm-l", pivot: ALP, rotation: [
        { t: 0, value: [-1.2, 0, 0.5] }, { t: 2.5, value: [-1.2, 0, 0.5] }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [-1.2, 0, -0.5] }, { t: 2.5, value: [-1.2, 0, -0.5] }] },
      { target: "head", pivot: HP, rotation: [
        { t: 0, value: [0.15, 0, 0] }, { t: 1.25, value: [0.2, 0, 0], ease: E },
        { t: 2.5, value: [0.15, 0, 0], ease: E }] },
    ] },
    // ── 50. victory ──
    { id: "victory", label: "Victory pose", duration: 2.0, loop: false, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.4, value: [-0.06, 0, 0], ease: E },
        { t: 1.6, value: [-0.06, 0, 0] }, { t: 2.0, value: [0, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.4, value: [-2.8, 0, 0.3], ease: E },
        { t: 1.6, value: [-2.8, 0, 0.3] }, { t: 2.0, value: [0, 0, 0], ease: E }] },
      { target: "arm-l", pivot: ALP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.4, value: [0, 0, -0.3], ease: E },
        { t: 1.6, value: [0, 0, -0.3] }, { t: 2.0, value: [0, 0, 0], ease: E }] },
      { target: "head", pivot: HP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.4, value: [-0.2, 0, 0], ease: E },
        { t: 1.6, value: [-0.2, 0, 0] }, { t: 2.0, value: [0, 0, 0], ease: E }] },
      { target: "head", pivot: HP, position: [
        { t: 0, value: [0, 0, 0] }, { t: 0.4, value: [0, 0.01, 0], ease: E },
        { t: 1.6, value: [0, 0.01, 0] }, { t: 2.0, value: [0, 0, 0], ease: E }] },
    ] },
  ],
});
